import { toast } from "@/components/ui/use-toast";
import { usePercentageStore } from "@/dashboard/store/store";
import { refreshCachedDataAfterMutation } from "@/lib/queryClient";
import { animatePercentage } from "@/libs/utils";
import { useCallback, useState } from "react";
interface PostDataOptions<T> {
  url: string;
  data: T;
  skipCacheRefresh?: boolean;
}
interface PostDataResponse {
  success: boolean;
  message?: string;
  data?: any;
}
interface UsePostDataResult<T> {
  postData: (options: PostDataOptions<T>) => Promise<any>;
  loading: boolean;
  error?: string;
  response?: PostDataResponse;
}
function usePostData<T>(): UsePostDataResult<T> {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [response, setResponse] = useState<PostDataResponse | undefined>(
    undefined
  );
  const { setPercentage, resetPercentage } = usePercentageStore();
  const postData = useCallback(
    async ({ url, data, skipCacheRefresh }: PostDataOptions<T>): Promise<any> => {
      setLoading(true);
      setError(undefined);
      setResponse(undefined);
      const token =
        localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
      try {
        // Démarrer l'animation du pourcentage
        animatePercentage(100, setPercentage);
        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        const res = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        });
        const result = await res.json();
        if (res.status === 400) {
          const message = result.error.violations[0].title;
          toast({
            className: "text-red-500",
            title: message,
          });
        }
        if (
          res.status === 422 &&
          Array.isArray(result) &&
          Array.isArray(result[0]) &&
          result[0].length === 2
        ) {
          toast({
            className: "text-red-500",
            title: result[0][1],
          });
        }
        if (!res.ok) {
          throw new Error(result.message || "Une erreur s’est produite");
        }
        setResponse({
          success: true,
          message: result.message,
          data: result,
        });
        if (!skipCacheRefresh) {
          void refreshCachedDataAfterMutation();
        }
        setPercentage(100); // S'assurer que l'animation atteint 100%.
        setTimeout(() => resetPercentage(), 1000); // Réinitialiser après une seconde.
        return result;
      } catch (err: any) {
        setError(err.message || "An unknown error occurred");
        setResponse({
          success: false,
          message: err.message,
        });
        return null;
      } finally {
        setLoading(false);
        setPercentage(100); // S'assurer que l'animation atteint 100%.
        setTimeout(() => resetPercentage(), 1000); // Réinitialiser après une seconde.
      }
    },
    [setPercentage, resetPercentage]
  );
  return { postData, loading, error, response };
}
export default usePostData;
